import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import api from '../services/api';
import '../styles/AdminDashboard.css';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Tooltip, Legend, Filler,
);

const S = n => `S/ ${(parseFloat(n) || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const N = n => (parseFloat(n) || 0).toLocaleString('es-PE');

function PctBadge({ val }) {
  const v = parseFloat(val) || 0;
  if (v === 0) return <span className="adsh-pct-zero">sin cambio</span>;
  return (
    <span className={`adsh-pct ${v > 0 ? 'adsh-pct-up' : 'adsh-pct-dn'}`}>
      {v > 0 ? '▲' : '▼'} {Math.abs(v).toFixed(1)}%
    </span>
  );
}

const CHART_COLORS = {
  blue: '#3b82f6',
  blueAlpha: 'rgba(59,130,246,0.12)',
  slate: '#94a3b8',
  slateAlpha: 'rgba(148,163,184,0.45)',
};

const baseScaleOpts = {
  x: { grid: { display: false }, ticks: { font: { size: 10 }, color: '#94a3b8' } },
  y: {
    grid: { color: '#f1f5f9' },
    ticks: {
      font: { size: 10 }, color: '#94a3b8',
      callback: v => v >= 1000 ? `S/${(v / 1000).toFixed(0)}k` : `S/${v}`,
    },
  },
};

export default function AdminDashboard() {
  const [periodo, setPeriodo] = useState('hoy');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [updAt, setUpdAt]     = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get(`/api/dashboard/resumen?periodo=${periodo}`);
      setData(r.data);
      setUpdAt(new Date());
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => { load(); }, [load]);

  /* ── Desestructurar ── */
  const k    = data?.kpis                    || {};
  const rq   = data?.resumen_rapido          || {};
  const cond = data?.ventas_por_condicion    || [];
  const movEf= data?.movimientos_efectivo    || { ingresos: { monto: 0, cuenta: 0 }, egresos: { monto: 0, cuenta: 0 } };
  const cob  = data?.cobros_cxc              || { items: [], total: 0 };
  const pag  = data?.pagos_cxp              || { items: [], total: 0 };
  const spark= data?.spark7                  || Array(7).fill(0);
  const ingD = data?.ingresos_diarios        || { labels: [], actual: [], anterior: [], labelActual: '', labelAnterior: '' };
  const ingM = data?.ingresos_mensuales      || { labels: [], actual: [], anterior: [], labelActual: '', labelAnterior: '' };

  /* ── Sparkline labels ── */
  const sparkLabels = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i));
    return d.toLocaleDateString('es-PE', { weekday: 'short', day: 'numeric' });
  });

  const sparkCfg = {
    data: {
      labels: sparkLabels,
      datasets: [{
        label: 'Ventas', data: spark,
        borderColor: CHART_COLORS.blue, backgroundColor: CHART_COLORS.blueAlpha,
        fill: true, tension: 0.4, pointRadius: 3, pointBackgroundColor: CHART_COLORS.blue,
      }],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: c => S(c.raw) } } },
      scales: baseScaleOpts,
    },
  };

  const ingDCfg = {
    data: {
      labels: ingD.labels,
      datasets: [
        {
          label: ingD.labelActual || 'Mes actual', data: ingD.actual,
          borderColor: CHART_COLORS.blue, backgroundColor: CHART_COLORS.blueAlpha,
          fill: true, tension: 0.3, pointRadius: 2, pointBackgroundColor: CHART_COLORS.blue,
        },
        {
          label: ingD.labelAnterior || 'Mes anterior', data: ingD.anterior,
          borderColor: CHART_COLORS.slate, backgroundColor: 'transparent',
          fill: false, tension: 0.3, pointRadius: 0, borderDash: [4, 3],
        },
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { position: 'top', labels: { font: { size: 11 }, boxWidth: 14, color: '#64748b' } },
        tooltip: { callbacks: { label: c => `${c.dataset.label}: ${S(c.raw)}` } } },
      scales: baseScaleOpts,
    },
  };

  const ingMCfg = {
    data: {
      labels: ingM.labels,
      datasets: [
        {
          label: ingM.labelActual || String(new Date().getFullYear()), data: ingM.actual,
          backgroundColor: 'rgba(59,130,246,0.75)', borderRadius: 4,
        },
        {
          label: ingM.labelAnterior || String(new Date().getFullYear() - 1), data: ingM.anterior,
          backgroundColor: CHART_COLORS.slateAlpha, borderRadius: 4,
        },
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { position: 'top', labels: { font: { size: 11 }, boxWidth: 14, color: '#64748b' } },
        tooltip: { callbacks: { label: c => `${c.dataset.label}: ${S(c.raw)}` } } },
      scales: baseScaleOpts,
    },
  };

  const totalCond  = cond.reduce((s, r) => s + r.monto, 0);
  const contado    = cond.find(r => r.condicion === 'CONTADO')  || { monto: 0, cuenta: 0 };
  const credito    = cond.find(r => r.condicion === 'CRÉDITO' || r.condicion === 'CREDITO') || { monto: 0, cuenta: 0 };
  const pctContado = totalCond > 0 ? Math.round(contado.monto / totalCond * 100) : 0;
  const pctCredito = totalCond > 0 ? Math.round(credito.monto / totalCond * 100) : 0;

  const PERIOD_LABELS = { hoy: 'Hoy', semana: 'Esta semana', mes: 'Este mes', anio: 'Este año' };

  return (
    <div className="adsh-wrap">

      {/* ── Header ── */}
      <div className="adsh-header">
        <div className="adsh-title-row">
          <h2 className="adsh-title">Dashboard</h2>
          {updAt && (
            <span className="adsh-upd">
              actualizado {updAt.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        <div className="adsh-controls">
          <div className="adsh-period-tabs">
            {['hoy', 'semana', 'mes', 'anio'].map(p => (
              <button key={p}
                className={`adsh-ptab${periodo === p ? ' adsh-ptab-active' : ''}`}
                onClick={() => setPeriodo(p)}>
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
          <button className={`adsh-refresh${loading ? ' adsh-refresh-spin' : ''}`}
            onClick={load} title="Actualizar" disabled={loading}>↻</button>
        </div>
      </div>

      {loading && <div className="adsh-loading-bar"><div /></div>}
      {error   && <div className="adsh-error">⚠ {error}</div>}

      {/* ── KPI Cards ── */}
      <div className="adsh-kpi-grid">
        <div className="adsh-kpi adsh-kpi-blue">
          <div className="adsh-kpi-ico">🛒</div>
          <div className="adsh-kpi-body">
            <div className="adsh-kpi-lbl">Ventas</div>
            <div className="adsh-kpi-val">{S(k.total_ventas)}</div>
            <div className="adsh-kpi-sub">
              <span>{N(k.total_ventas_count)} transac.</span>
              <PctBadge val={k.total_ventas_pct} />
            </div>
          </div>
        </div>

        <div className="adsh-kpi adsh-kpi-indigo">
          <div className="adsh-kpi-ico">📦</div>
          <div className="adsh-kpi-body">
            <div className="adsh-kpi-lbl">Compras</div>
            <div className="adsh-kpi-val">{S(k.total_compras)}</div>
            <div className="adsh-kpi-sub">
              <span>{N(k.total_compras_count)} órdenes</span>
              <PctBadge val={k.total_compras_pct} />
            </div>
          </div>
        </div>

        <div className="adsh-kpi adsh-kpi-violet">
          <div className="adsh-kpi-ico">📊</div>
          <div className="adsh-kpi-body">
            <div className="adsh-kpi-lbl">Productos vendidos</div>
            <div className="adsh-kpi-val">{N(k.productos_vendidos)}</div>
            <div className="adsh-kpi-sub"><span>unidades despachadas</span></div>
          </div>
        </div>

        <div className="adsh-kpi adsh-kpi-rose">
          <div className="adsh-kpi-ico">🚫</div>
          <div className="adsh-kpi-body">
            <div className="adsh-kpi-lbl">Ventas anuladas</div>
            <div className="adsh-kpi-val">{N(k.ventas_anuladas)}</div>
            <div className="adsh-kpi-sub"><span>{S(k.ventas_anuladas_monto)}</span></div>
          </div>
        </div>
      </div>

      {/* ── Fila media ── */}
      <div className="adsh-mid-grid">

        {/* Condición de pago */}
        <div className="adsh-panel">
          <div className="adsh-panel-hd">
            <span className="adsh-panel-title">Ventas por condición</span>
            <span className="adsh-panel-badge">{PERIOD_LABELS[periodo]}</span>
          </div>
          <div className="adsh-cond-list">
            {[
              { label: 'Contado', data: contado, pct: pctContado, cls: 'adsh-dot-blue' },
              { label: 'Crédito', data: credito, pct: pctCredito, cls: 'adsh-dot-slate' },
            ].map(({ label, data: d, pct, cls }) => (
              <div key={label} className="adsh-cond-row">
                <span className={`adsh-dot ${cls}`} />
                <span className="adsh-cond-name">{label}</span>
                <span className="adsh-cond-cnt">{N(d.cuenta)}</span>
                <div className="adsh-bar-bg">
                  <div className={`adsh-bar ${cls}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="adsh-cond-pct">{pct}%</span>
                <span className="adsh-cond-monto">{S(d.monto)}</span>
              </div>
            ))}
          </div>
          <div className="adsh-cond-total">Total: <strong>{S(totalCond)}</strong></div>
        </div>

        {/* Efectivo */}
        <div className="adsh-panel">
          <div className="adsh-panel-hd">
            <span className="adsh-panel-title">Movimientos de caja</span>
            {rq.caja_desde && (
              <span className="adsh-caja-tag">
                Abierta {rq.caja_desde.hora} · {rq.caja_desde.elapsed}
              </span>
            )}
          </div>
          <div className="adsh-ef-list">
            <div className="adsh-ef-row adsh-ef-ing">
              <span className="adsh-ef-arrow">↑</span>
              <span className="adsh-ef-lbl">Ingresos</span>
              <span className="adsh-ef-cnt">{N(movEf.ingresos?.cuenta)} mov.</span>
              <span className="adsh-ef-monto">{S(movEf.ingresos?.monto)}</span>
            </div>
            <div className="adsh-ef-row adsh-ef-egr">
              <span className="adsh-ef-arrow">↓</span>
              <span className="adsh-ef-lbl">Egresos</span>
              <span className="adsh-ef-cnt">{N(movEf.egresos?.cuenta)} mov.</span>
              <span className="adsh-ef-monto">{S(movEf.egresos?.monto)}</span>
            </div>
            <div className="adsh-ef-neto">
              <span>Neto</span>
              <span>{S((parseFloat(movEf.ingresos?.monto) || 0) - (parseFloat(movEf.egresos?.monto) || 0))}</span>
            </div>
          </div>
        </div>

        {/* CxC Cobros */}
        <div className="adsh-panel">
          <div className="adsh-panel-hd">
            <span className="adsh-panel-title">Cobros CxC</span>
            <span className="adsh-panel-total">{S(cob.total)}</span>
          </div>
          <div className="adsh-fp-list">
            {cob.items.length === 0
              ? <div className="adsh-fp-empty">Sin cobros en el período</div>
              : cob.items.map(r => (
                <div key={r.forma_pago} className="adsh-fp-row">
                  <span className="adsh-fp-name">{r.forma_pago}</span>
                  <span className="adsh-fp-cnt">{r.cuenta}</span>
                  <span className="adsh-fp-monto">{S(r.monto)}</span>
                </div>
              ))
            }
          </div>
        </div>

        {/* CxP Pagos */}
        <div className="adsh-panel">
          <div className="adsh-panel-hd">
            <span className="adsh-panel-title">Pagos CxP</span>
            <span className="adsh-panel-total">{S(pag.total)}</span>
          </div>
          <div className="adsh-fp-list">
            {pag.items.length === 0
              ? <div className="adsh-fp-empty">Sin pagos en el período</div>
              : pag.items.map(r => (
                <div key={r.forma_pago} className="adsh-fp-row">
                  <span className="adsh-fp-name">{r.forma_pago}</span>
                  <span className="adsh-fp-cnt">{r.cuenta}</span>
                  <span className="adsh-fp-monto">{S(r.monto)}</span>
                </div>
              ))
            }
          </div>
        </div>

      </div>

      {/* ── Gráficos ── */}
      <div className="adsh-charts-grid">
        <div className="adsh-chart-panel">
          <div className="adsh-panel-title">Ventas — últimos 7 días</div>
          <div className="adsh-chart-box">
            <Line data={sparkCfg.data} options={sparkCfg.options} />
          </div>
        </div>

        <div className="adsh-chart-panel adsh-chart-wide">
          <div className="adsh-panel-title">Ingresos diarios</div>
          <div className="adsh-chart-box">
            <Line data={ingDCfg.data} options={ingDCfg.options} />
          </div>
        </div>

        <div className="adsh-chart-panel adsh-chart-wide">
          <div className="adsh-panel-title">Ingresos mensuales</div>
          <div className="adsh-chart-box">
            <Bar data={ingMCfg.data} options={ingMCfg.options} />
          </div>
        </div>
      </div>

      {/* ── Resumen rápido ── */}
      <div className="adsh-rq-grid">
        <div className="adsh-rq-card">
          <div className="adsh-rq-ico">📈</div>
          <div className="adsh-rq-lbl">Margen estimado</div>
          <div className="adsh-rq-val">{(parseFloat(rq.margen_estimado) || 0).toFixed(1)}%</div>
        </div>
        <div className="adsh-rq-card">
          <div className="adsh-rq-ico">🏷️</div>
          <div className="adsh-rq-lbl">Productos activos</div>
          <div className="adsh-rq-val">{N(rq.productos_activos)}</div>
        </div>
        <div className="adsh-rq-card">
          <div className="adsh-rq-ico">👥</div>
          <div className="adsh-rq-lbl">Clientes con deuda</div>
          <div className="adsh-rq-val">{N(rq.clientes_con_deuda)}</div>
        </div>
        <div className={`adsh-rq-card${rq.stock_bajo_minimo > 0 ? ' adsh-rq-warn' : ''}`}>
          <div className="adsh-rq-ico">⚠️</div>
          <div className="adsh-rq-lbl">Stock bajo mínimo</div>
          <div className="adsh-rq-val">{N(rq.stock_bajo_minimo)}</div>
        </div>
        {!rq.caja_desde && (
          <div className="adsh-rq-card adsh-rq-closed">
            <div className="adsh-rq-ico">🔒</div>
            <div className="adsh-rq-lbl">Caja</div>
            <div className="adsh-rq-val">Cerrada</div>
          </div>
        )}
      </div>

    </div>
  );
}
